import { RowButton } from "@/components/ui/pressable";
import { useEffect, useMemo, useState } from "react";
import {
  useGetIdentity,
  useListContext,
  useListFilterContext,
  type Identifier,
} from "ra-core";
import { Building2, Plus } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableHeader, TableRow } from "@/components/ui/table";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { CompanyAvatar } from "@/components/atomic-crm/companies/CompanyAvatar";
import type { Company } from "@/components/atomic-crm/types";
import {
  resolveCompanyEmailForDisplay,
  resolveCompanyPhoneRaw,
} from "@/modules/clients/companyChannelResolvers";
import { OpenMailComposeLink } from "@/modules/mail/OpenMailComposeLink";
import { CrmPhoneLink } from "@/modules/voice/CrmPhoneLink";
import { useIsMobile } from "@/hooks/use-mobile";
import { IconButton } from "@/components/ui/icon-button";
import { MobilePageChrome } from "@/components/atomic-crm/layout/MobilePageChrome";
import { getClientShowPath, getPersonShowPath } from "@/app/routing";
import { buildAccountsCompanyPreviewParams } from "@/modules/accounts/AccountsRecordPanel";
import { buildAccountsPersonPreviewParams } from "@/modules/accounts/AccountsLeadPreviewSheet";
import {
  AccountsListTabToggle,
  AccountsModuleToolbar,
  type AccountsHubChrome,
} from "@/modules/accounts/AccountsModuleToolbar";
import { formatCompanyCityState } from "@/modules/accounts/accountsListFilterUtils";
import { useOrgModules } from "@/modules/useOrgModules";
import {
  GridCells,
  GridRowEnd,
  GridHeaderRow,
  GRID_LIST_ROW_CLASS,
  useDataGrid,
  useGridServerFilters,
  type GridColumn,
} from "@/modules/shared/dataGrid";
import { ModuleSearchField } from "@/components/atomic-crm/layout/ModuleToolbar";
import { CONTACT_STATUS_CHOICES } from "@/modules/constants/contactStatus";
import { cn } from "@/lib/utils";

const normalizeWebsiteHref = (website?: string | null) => {
  const trimmed = String(website ?? "").trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

const primaryContactName = (company: Company) => {
  const name = [
    company.primary_contact_first_name,
    company.primary_contact_last_name,
  ]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return name || null;
};

const statusLabel = (company: Company) => {
  if (company.is_client) return "Client";
  const raw = String(company.primary_contact_status ?? "")
    .trim()
    .toLowerCase();
  if (!raw) return null;
  if (raw === "lead" || raw === "prospect") {
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  if (raw === "client") return "Client";
  return (
    CONTACT_STATUS_CHOICES.find((choice) => choice.value === raw)?.label ??
    raw.charAt(0).toUpperCase() + raw.slice(1)
  );
};

const statusBadgeClass = (company: Company, status: string) => {
  if (company.is_client || status === "Client") {
    return "border-success/40 bg-success/10 text-success-text";
  }
  if (status === "Lead") {
    return "border-violet-300/70 bg-violet-50 text-violet-800 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-100";
  }
  if (status === "Prospect") {
    return "border-info/40 bg-info/10 text-info-text";
  }
  return "text-muted-foreground";
};

const muted = (value: unknown) =>
  value == null || String(value).trim() === "" ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="text-sm text-muted-foreground">{String(value)}</span>
  );

const formatDate = (value: unknown) =>
  value
    ? new Date(String(value)).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

/** Links inside a clickable row must not open the row. */
const stop = (event: React.MouseEvent) => event.stopPropagation();

/** Every column Companies can show; some are filters only. */
const companyColumns = ({
  pipelineOn,
  onOpenPerson,
}: {
  pipelineOn: boolean;
  onOpenPerson: (company: Company) => void;
}): GridColumn<Company>[] => {
  const columns: GridColumn<Company>[] = [
    {
      id: "name",
      label: "Business name",
      type: "text",
      sortable: true,
      filterable: true,
      alwaysVisible: true,
      cell: (company) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <CompanyAvatar record={company} width={25} height={25} />
          <span className="truncate text-foreground">
            {company.name?.trim() || "Untitled company"}
          </span>
        </div>
      ),
    },
    {
      id: "primary_contact",
      label: "Primary contact",
      type: "text",
      cell: (company) => {
        const contactName = primaryContactName(company);
        return contactName && company.primary_contact_id ? (
          <RowButton
            layout="none"
            density="none"
            className="text-left font-medium text-foreground underline-offset-2 transition-colors hover:text-primary hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              onOpenPerson(company);
            }}
          >
            {contactName}
          </RowButton>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "phone",
      label: "Phone",
      type: "text",
      field: "phone_number",
      filterable: true,
      cell: (company) => (
        <span onClick={stop}>
          <CrmPhoneLink
            phone={resolveCompanyPhoneRaw(company)}
            contactId={company.primary_contact_id}
            className="text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline"
          />
        </span>
      ),
    },
    {
      id: "email",
      label: "Email",
      type: "text",
      cell: (company) => {
        const email = resolveCompanyEmailForDisplay(company);
        return email && email !== "—" ? (
          <span onClick={stop}>
            <OpenMailComposeLink
              to={email}
              companyId={company.id}
              contactId={company.primary_contact_id ?? undefined}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline"
            >
              {email}
            </OpenMailComposeLink>
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "website",
      label: "Website",
      type: "text",
      sortable: true,
      filterable: true,
      cell: (company) => {
        const website = String(company.website ?? "").trim();
        const websiteHref = normalizeWebsiteHref(website);
        return website && websiteHref ? (
          <a
            href={websiteHref}
            target="_blank"
            rel="noreferrer"
            onClick={stop}
            className="text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline"
          >
            {website}
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "location",
      label: "Location",
      type: "text",
      field: "city",
      sortable: true,
      cell: (company) => {
        const location = formatCompanyCityState(company);
        return (
          <span
            className={cn(
              "text-sm",
              location === "—" ? "text-muted-foreground" : "text-foreground",
            )}
          >
            {location}
          </span>
        );
      },
    },
    {
      id: "status",
      label: "Status",
      type: "text",
      field: "is_client",
      sortable: true,
      cell: (company) => {
        const status = statusLabel(company);
        return status ? (
          <Badge
            variant="outline"
            className={cn(
              "text-2xs font-medium",
              statusBadgeClass(company, status),
            )}
          >
            {status}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "sector",
      label: "Industry",
      type: "text",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cell: (company) => muted(company.sector),
    },
    {
      id: "created_at",
      label: "Added",
      type: "date",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      cell: (company) =>
        muted(formatDate((company as { created_at?: string }).created_at)),
    },
    {
      id: "nb_contacts",
      label: "People",
      type: "number",
      sortable: true,
      filterable: true,
      defaultHidden: true,
      align: "right",
      cell: (company) => muted(company.nb_contacts),
    },
    {
      id: "is_client",
      label: "Client",
      type: "boolean",
      filterable: true,
      filterOnly: true,
      cell: () => null,
    },
    {
      id: "primary_contact_status",
      label: "Contact status",
      type: "select",
      filterable: true,
      filterOnly: true,
      options: CONTACT_STATUS_CHOICES.map((choice) => ({
        value: choice.value,
        label: choice.label,
      })),
      cell: () => null,
    },
    {
      id: "city",
      label: "City",
      type: "text",
      filterable: true,
      filterOnly: true,
      cell: () => null,
    },
    {
      id: "state_abbr",
      label: "State",
      type: "text",
      filterable: true,
      filterOnly: true,
      cell: () => null,
    },
  ];
  if (pipelineOn) {
    columns.push(
      {
        id: "nb_deals",
        label: "Deals",
        type: "number",
        sortable: true,
        filterable: true,
        defaultHidden: true,
        align: "right",
        cell: (company) => muted(company.nb_deals),
      },
      {
        id: "has_website_project",
        label: "Has website project",
        type: "boolean",
        filterable: true,
        filterOnly: true,
        cell: () => null,
      },
    );
  }
  return columns;
};

/** Company-first Accounts List — bill-to directory with primary contact context. */
export const AccountsCompaniesList = ({
  accountsChrome,
}: {
  accountsChrome: AccountsHubChrome;
}) => {
  const { identity } = useGetIdentity();
  if (!identity) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <List
        resource="companies"
        title={false}
        disableBreadcrumb
        perPage={25}
        sort={{ field: "name", order: "ASC" }}
        actions={false}
        contentScrollable={false}
        // Keep list params out of the URL: ra-core would otherwise store every
        // query param (e.g. ?company= of an open preview) and restore it when
        // the user comes back to Accounts, reopening that preview.
        disableSyncWithLocation
        storeKey="accounts.companies"
        className="min-h-0 flex-1"
        // Same 16px gap as the toolbar above the table card.
        paginationClassName="mt-4 border-t-0 bg-transparent px-0"
        pagination={<ListPagination rowsPerPageOptions={[10, 25, 50, 100]} />}
      >
        <AccountsCompaniesListBody accountsChrome={accountsChrome} />
      </List>
    </div>
  );
};

const AccountsCompaniesListBody = ({
  accountsChrome,
}: {
  accountsChrome: AccountsHubChrome;
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data = [], total, isPending } = useListContext<Company>();
  const { filterValues, setFilters } = useListFilterContext();
  const [searchDraft, setSearchDraft] = useState(() =>
    String(filterValues?.q ?? ""),
  );
  const pipelineOn = useOrgModules().isEnabled("pipeline");

  useEffect(() => {
    setSearchDraft(String(filterValues?.q ?? ""));
  }, [filterValues?.q]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchDraft.trim();
      const current = String(filterValues?.q ?? "").trim();
      if (next === current) return;
      const nextFilters = { ...filterValues };
      if (next) nextFilters.q = next;
      else delete nextFilters.q;
      setFilters(nextFilters, undefined, false);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchDraft, filterValues, setFilters]);

  const openCompany = (companyId: Identifier) => {
    if (isMobile) {
      navigate(getClientShowPath(companyId));
      return;
    }
    setSearchParams(
      buildAccountsCompanyPreviewParams(searchParams, companyId),
      {
        replace: true,
      },
    );
  };

  const openPerson = (
    contact: Pick<Company, "primary_contact_id" | "primary_contact_status">,
  ) => {
    if (!contact.primary_contact_id) return;
    if (isMobile) {
      navigate(
        getPersonShowPath({
          id: contact.primary_contact_id,
          status: contact.primary_contact_status ?? "",
        }),
      );
      return;
    }
    setSearchParams(
      buildAccountsPersonPreviewParams(searchParams, {
        id: contact.primary_contact_id,
        status: contact.primary_contact_status ?? "",
        lead_stage: null,
      }),
      { replace: true },
    );
  };

  const columns = useMemo(
    () => companyColumns({ pipelineOn, onOpenPerson: openPerson }),
    // openPerson only reads the URL setter and navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pipelineOn],
  );
  const grid = useDataGrid({ tableId: "accounts.companies", columns });
  useGridServerFilters(columns, grid.filters, grid.isLoaded);
  const hasFilters = Boolean(filterValues?.q) || grid.filters.length > 0;

  const searchField = (
    <ModuleSearchField
      value={searchDraft}
      onChange={setSearchDraft}
      basePlaceholder="Search name, email, phone, city, or state"
      total={total}
      itemSingular="company"
      itemPlural="companies"
    />
  );

  const toolbar = (
    <div className="flex flex-col gap-2">
      <AccountsModuleToolbar
        {...accountsChrome}
        leadingExtra={
          <div className="flex min-w-0 items-center gap-2">
            {searchField}
            {grid.filterButton}
            {grid.columnsButton}
          </div>
        }
      />
      {grid.chips}
    </div>
  );

  const mobileCreate = accountsChrome.onNewCompany ? (
    <IconButton aria-label="New company" onClick={accountsChrome.onNewCompany}>
      <Plus className="size-6" />
    </IconButton>
  ) : undefined;

  const mobileSearch = (
    <div className="space-y-2">
      {accountsChrome.onListTabChange ? (
        <AccountsListTabToggle
          value={accountsChrome.listTab ?? "companies"}
          onChange={accountsChrome.onListTabChange}
        />
      ) : null}
      {searchField}
      <div className="flex items-center gap-2">{grid.fullFilterButton}</div>
      {grid.chips}
    </div>
  );

  if (isPending) return null;

  if (!data.length && !hasFilters) {
    if (isMobile) {
      return (
        <MobilePageChrome
          title="Accounts"
          action={mobileCreate}
          search={mobileSearch}
        >
          <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
            <Building2 className="size-10 text-muted-foreground" />
            <div className="space-y-1">
              <h3 className="text-base font-semibold">No companies yet</h3>
              <p className="max-w-sm text-sm text-muted-foreground">
                Add a company to start your Accounts directory. People stay
                linked under each company preview.
              </p>
            </div>
          </div>
        </MobilePageChrome>
      );
    }
    return (
      <div className="space-y-3">
        {toolbar}
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <Building2 className="size-10 text-muted-foreground" />
          <div className="space-y-1">
            <h3 className="text-base font-semibold">No companies yet</h3>
            <p className="max-w-sm text-sm text-muted-foreground">
              Add a company to start your Accounts directory. People stay linked
              under each company preview.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const emptyFilteredMessage = "No companies match these filters.";

  if (isMobile) {
    return (
      <MobilePageChrome
        title="Accounts"
        action={mobileCreate}
        search={mobileSearch}
      >
        {!data.length ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            {emptyFilteredMessage}
          </p>
        ) : (
          <ul className="glass-grouped">
            {data.map((company) => {
              const contactName = primaryContactName(company);
              const status = statusLabel(company);
              const phone = resolveCompanyPhoneRaw(company);
              const location = formatCompanyCityState(company);
              return (
                <li key={String(company.id)}>
                  <RowButton
                    layout="none"
                    density="none"
                    className="flex w-full items-start gap-3 px-1 py-3.5 text-left transition-colors hover:bg-black/[0.03] active:bg-black/[0.05] dark:hover:bg-white/[0.04]"
                    onClick={() => openCompany(company.id)}
                  >
                    <CompanyAvatar record={company} width={36} />
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <span className="truncate text-foreground">
                          {company.name?.trim() || "Untitled company"}
                        </span>
                        {status ? (
                          <Badge
                            variant="outline"
                            className={cn(
                              "shrink-0 text-2xs font-medium",
                              statusBadgeClass(company, status),
                            )}
                          >
                            {status}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="truncate text-sm text-muted-foreground">
                        {contactName || phone || "No primary contact"}
                      </div>
                      {location !== "—" ? (
                        <div className="truncate text-xs text-muted-foreground">
                          {location}
                        </div>
                      ) : null}
                    </div>
                  </RowButton>
                </li>
              );
            })}
          </ul>
        )}
      </MobilePageChrome>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <div className="shrink-0">{toolbar}</div>

      {!data.length ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {emptyFilteredMessage}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <GridHeaderRow grid={grid.header} columns={grid.visible} />
            </TableHeader>
            <TableBody>
              {data.map((company) => (
                <TableRow
                  key={String(company.id)}
                  className={cn("cursor-pointer", GRID_LIST_ROW_CLASS)}
                  onClick={() => openCompany(company.id)}
                >
                  <GridCells columns={grid.visible} row={company} />
                  <GridRowEnd />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
